import { FunctionComponent, useState, useEffect, CSSProperties } from 'react'
import { Box, Typography, IconButton, Link } from '@material-ui/core'
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom'
import { BackendUser } from 'common/types'
import { useTheme } from 'styled-components'
import styled from 'styled-components/macro'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { AddCircle } from '@material-ui/icons'
import { ReactComponent as HomeIcon } from 'icons/home.svg'
import { ReactComponent as NetworkIcon } from 'icons/people.svg'
import { ReactComponent as BriefcaseIcon } from 'icons/briefcase-solid.svg'
import { useThemeSwitch } from 'hooks/theme'
import { CreatePostModal } from 'pages/content/components/posts/create-post'
import { useFooterNav } from './hooks/useFooterNav'
import { useNotification } from 'hooks/use-notification/useNotification'
import { NavIconForChat } from 'components/page/direct-messaging'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useMessages } from 'hooks/use-messages/useMessages'
import { MainWrapperNavBarLink } from '../containers/MainWrapperNavBarLink'
import { PathName } from 'common/types/pathName.types'
import { NavButton } from 'common/atoms/NavButton/NavButton'

const NavWrapper = styled(Box)<{ isFixed: boolean }>`
  && {
    z-index: 1201;
    background-color: ${props => props.theme.colors.supplementary};
    position: ${props => (props.isFixed ?? true ? 'fixed' : 'static')};
    bottom: 0;
    width: 100%;
    height: 4.5em;
    height: 60px;
  }
`
const StyledNavInnerWrapper = styled(Box)`
  && {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    align-content: center;
    height: 100%;
  }

  && > * {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const StyledHomeIcon = styled(HomeIcon)<{ active?: boolean }>`
  cursor: pointer;
  transform: scale(1);
  fill: ${props => (props.active ? props.theme.colors.primary : props.theme.colors.accent)};
`

const StyledNetworkIcon = styled(NetworkIcon)`
  cursor: pointer;
  transform: scale(1);
`

const StyledBriefcaseIcon = styled(BriefcaseIcon)`
  && {
    transform: scale(1);
  }
`

const AddPostButton = styled(AddCircle)<{ pathname?: string }>`
  && {
    fill: ${props =>
      props.pathname === PathName.CREATE_POST ? props.theme.colors.primary : props.theme.colors.accent};
    transform: scale(1);
  }
`

interface FooterNavProps {
  user: BackendUser
  isFixed?: boolean
  style?: CSSProperties
}

export const FooterNav: FunctionComponent<FooterNavProps> = ({ user, isFixed, style }) => {
  const theme = useTheme()
  const { logEventAnalytics } = useAnalytics()
  const { mode } = useThemeSwitch()
  const [show, setShow] = useState(false)
  const [showSuccessChip, setShowSuccessChip] = useState(false)
  const { createContent, setShowLoader, forceUpdate } = useFooterNav()
  const { notify } = useNotification()
  const history = useHistory()
  const { backendPartner } = useAuth()
  const { messages, showNotification, setShowNotification } = useMessages()
  const { pathname } = useLocation()

  useEffect(() => {
    if (showSuccessChip) {
      notify('success', 'Posted Successfully')
    }
  }, [showSuccessChip])

  return (
      <NavWrapper isFixed={isFixed} style={style}>
        <StyledNavInnerWrapper>
          <NavButton
            onClick={() => {
              forceUpdate()
              const partnerLog = {}
              const userLog = {}
              userLog['User ID'] = user?.id
              if (user?.partnerId) {
                partnerLog['Partner ID'] = backendPartner?.id
                partnerLog['Company Name'] = backendPartner?.name
                partnerLog['Account Type'] = 'Partner'
              } else {
                userLog['Account Type'] = user?.role
              }
              logEventAnalytics(
                user?.partnerId ? 'Partner' : 'Member',
                'Posts viewed',
                'mixpanel',
                'Posts viewed',
                { ...partnerLog, ...userLog },
                user?.id
              )
            }}
            icon={<StyledHomeIcon active={pathname === PathName.FEED} />}
            label="Tea Spot"
            to={PathName.FEED}
          />
          <NavButton
            to={PathName.CONNECTIONS}
            onClick={() => {
              logEventAnalytics(
                user?.partnerId ? 'Partner' : user?.role,
                'View Connections',
                'both',
                'View Connections',
                {
                  'Connection viewer email': user?.username,
                  'Connection viewer ID': user?.id,
                  'Account type': user?.partnerId ? 'Partner' : user?.role
                },
                user?.id,
                {
                  $name: `${user?.firstName} ${user?.lastName}`,
                  $email: user?.username
                }
              )
            }}
            icon={
              <StyledNetworkIcon
                fill={pathname === PathName.CONNECTIONS ? theme.colors.primary : theme.colors.accent}
              />
            }
            label="Tribe"
          />
          <NavButton
            to={PathName.PRIVATE_JOBS}
            icon={
              <Box
                bgcolor={pathname === PathName.PRIVATE_JOBS ? theme.colors.primary : theme.colors.accent1}
                padding="1rem"
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderRadius="50%"
                position="absolute"
                bottom="1.5rem"
              >
                <StyledBriefcaseIcon
                  fill={pathname === PathName.PRIVATE_JOBS ? theme.colors.accent1 : theme.colors.supplementary}
                />
              </Box>
            }
            label="Jobs"
          />
          <NavIconForChat
            theme={theme}
            messages={messages}
            showNotification={showNotification}
            setShowNotification={setShowNotification}
            pathname={pathname}
          />
          <NavButton
            to="/createPost"
            icon={<AddPostButton pathname={pathname} fill={theme.colors.accent} />}
            label="Create Post"
          />
        </StyledNavInnerWrapper>
        <Box>
          <CreatePostModal
            show={show}
            setShow={setShow}
            setShowSuccessChip={setShowSuccessChip}
            title=""
            noborder
            onClose={() => {
              setShow(false)
            }}
            createContent={createContent}
            setShowLoader={setShowLoader}
            user={user}
            edit={false}
          />
        </Box>
      </NavWrapper>
  )
}
